import React from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import moment from 'moment-timezone';
import './EventDateRangeInput.scss';

import { Checkbox, DateRangeInput } from 'v1/components/shared';
import EventHoursInput, {
  TypeEventHours
} from '../EventHoursInput/EventHoursInput';
import { Button } from 'v5/design-system/Button.js';
import { Trash2 } from 'lucide-react';
import {
  DURATION_DEFAULTS,
  doesEventDateRangeOverlapWeekend
} from 'v1/helpers/byModel/EventHelper';

const EventDateRangeInput = ({
  className,
  appearance,
  size,
  dateFormat,
  isReadOnly,
  event,
  onChange,
  onRemove,
  labelStart,
  labelEnd,
  limits,
  filterDate,
  allowedRanges,
  highlightDates,
  hideTimePicker
}) => {
  const formatDate = date => {
    return moment
      .tz(date, moment.tz.guess())
      .format(
        event.date_type === 'INCLUSIVE_HOURS'
          ? 'YYYY-MM-DDTHH:mm:ssZ'
          : 'YYYY-MM-DD'
      );
  };

  const handleIncludeHours = (field, value, previous) => {
    handleChange(field, value, previous);
  };

  const handleChange = (field, value, previous) => {
    const timezone = moment().tz();
    let updatedDates = {};

    if (field === 'date_type') {
      switch (value) {
        case 'INCLUSIVE_HOURS':
          updatedDates = {
            start_timestamp: moment
              .tz(event.start_date, timezone)
              .hours(DURATION_DEFAULTS.INCLUSIVE_START_HOUR)
              .format(),
            end_timestamp: moment
              .tz(event.end_date, timezone)
              .hours(DURATION_DEFAULTS.INCLUSIVE_END_HOUR)
              .format()
          };
          break;
        case 'RECURRING_HOURS':
          updatedDates = {
            estimated_daily_hours: DURATION_DEFAULTS.ESTIMATED_DAILY_HOURS,
            estimated_daily_minutes: DURATION_DEFAULTS.ESTIMATED_DAILY_MINUTES
          };
          break;
        case 'RECURRING_SPECIFIC_HOURS':
          updatedDates = {
            estimated_start_time: DURATION_DEFAULTS.ESTIMATED_START_TIME,
            estimated_end_time: DURATION_DEFAULTS.ESTIMATED_END_TIME
          };
          break;
        default:
          if (previous === 'INCLUSIVE_HOURS') {
            updatedDates = {
              start_date: moment
                .tz(event.start_timestamp, timezone)
                .format('YYYY-MM-DD'),
              end_date: moment
                .tz(event.end_timestamp, timezone)
                .format('YYYY-MM-DD')
            };
          }
      }
    }

    onChange({ ...event, ...updatedDates, [field]: value });
  };

  const handleDateRangeChange = ({ from, to }) => {
    const startField =
      event.date_type === 'INCLUSIVE_HOURS' ? 'start_timestamp' : 'start_date';
    const endField =
      event.date_type === 'INCLUSIVE_HOURS' ? 'end_timestamp' : 'end_date';

    const update = {};

    // Handle start date change
    if (from) {
      const startDate = moment.tz(from, moment.tz.guess());
      const formattedStartDate = formatDate(from);
      update[startField] = formattedStartDate;

      // If start date is set after end date, update end date to match start date
      const currentEndDate = event[endField];
      if (
        currentEndDate &&
        startDate.isAfter(moment.tz(currentEndDate, moment.tz.guess()))
      ) {
        update[endField] = formattedStartDate;
      }
    }

    // Handle end date change - skip if end date is already adjusted by start date logic
    if (to && !update[endField]) {
      const endDate = moment.tz(to, moment.tz.guess());
      const formattedEndDate = formatDate(to);
      update[endField] = formattedEndDate;

      // If end date is set before start date, update start date to match end date
      const currentStartDate = event[startField];
      if (
        currentStartDate &&
        endDate.isBefore(moment.tz(currentStartDate, moment.tz.guess()))
      ) {
        update[startField] = formattedEndDate;
      }
    }

    onChange({ ...event, ...update });
  };

  const shouldRenderIncludeWeekends =
    event.date_type !== 'INCLUSIVE_HOURS' &&
    doesEventDateRangeOverlapWeekend(event) &&
    (!isReadOnly || event.include_weekends);

  return (
    <div
      className={classnames([
        'EventDateRangeInput  tailwind-root',
        {
          [`EventDateRangeInput-${size}`]: size
        },
        className
      ])}
    >
      <div
        className={classnames(
          'EventDateRangeInput-inner group/date-range-input flex flex-row gap-3'
        )}
      >
        <DateRangeInput
          changeOnDebounce
          size={size}
          appearance={appearance}
          required
          labelClassName="form-label"
          labels={{ start: labelStart, end: labelEnd }}
          dateFormat={
            event.date_type === 'INCLUSIVE_HOURS'
              ? 'eee dd MMM h:mmaa'
              : dateFormat
          }
          disabled={isReadOnly}
          showTimeSelect={event.date_type === 'INCLUSIVE_HOURS'}
          startDate={
            event.date_type === 'INCLUSIVE_HOURS'
              ? event.start_timestamp
              : event.start_date
          }
          endDate={
            event.date_type === 'INCLUSIVE_HOURS'
              ? event.end_timestamp
              : event.end_date
          }
          minDate={limits.minDate}
          maxDate={limits.maxDate}
          filterDate={filterDate}
          allowedRanges={allowedRanges}
          onValueChange={handleDateRangeChange}
          highlightDates={highlightDates}
          onIncludeHoursChange={checked => {
            handleIncludeHours(
              'date_type',
              checked ? 'INCLUSIVE_HOURS' : 'RECURRING_DEFAULT',
              event.date_type
            );
          }}
        >
          <Checkbox
            name="include_hours"
            label="Include hours"
            checked={event.date_type === 'INCLUSIVE_HOURS' || false}
            onChange={({ target }) =>
              handleIncludeHours(
                'date_type',
                target.checked ? 'INCLUSIVE_HOURS' : 'RECURRING_DEFAULT',
                event.date_type
              )
            }
          />
        </DateRangeInput>

        {!hideTimePicker && event.date_type !== 'INCLUSIVE_HOURS' && (
          <EventHoursInput
            className="ml-0!"
            size={size}
            appearance={appearance}
            date_type={event.date_type}
            estimated_daily_hours={event.estimated_daily_hours}
            estimated_daily_minutes={event.estimated_daily_minutes}
            estimated_start_time={event.estimated_start_time}
            estimated_end_time={event.estimated_end_time}
            disabled={isReadOnly}
            onChange={handleChange}
          />
        )}

        {onRemove && !isReadOnly && (
          <Button
            size="sm"
            variant="ghost"
            className="ml-auto opacity-0 group-hocus/date-range-input:opacity-100"
            layout="icon"
            onClick={onRemove}
          >
            <Trash2 className="text-fg-danger" />
          </Button>
        )}
      </div>

      {shouldRenderIncludeWeekends && (
        <Checkbox
          className={classnames([
            'EventDateRangeInput-weekends mt-3',
            {
              'EventDateRangeInput-weekends-show':
                event.include_weekends && event.date_type !== 'INCLUSIVE_HOURS'
            },
            {
              'EventDateRangeInput-weekends-hide':
                event.date_type === 'INCLUSIVE_HOURS' || isReadOnly
            }
          ])}
          name="include_weekends"
          label="Include weekends"
          size="S"
          checked={event.include_weekends || false}
          onChange={({ target }) =>
            handleChange('include_weekends', target.checked)
          }
        />
      )}
    </div>
  );
};

export const TypeEvent = PropTypes.shape({
  start_date: PropTypes.any, // '2019-09-23'
  end_date: PropTypes.any, // '2019-09-25'
  start_timestamp: PropTypes.any, // '2019-09-04T00:00:00.000Z' / Only for INCLUSIVE_HOURS
  end_timestamp: PropTypes.any, // '2019-09-06T22:30:00.000Z' / Only for INCLUSIVE_HOURS
  include_weekends: PropTypes.bool, // Only for RECURRING_DEFAULT, RECURRING_HOURS, RECURRING_SPECIFIC_HOURS
  ...TypeEventHours
});

EventDateRangeInput.propTypes = {
  className: PropTypes.string,
  dateFormat: PropTypes.string, // E.g. 'dd-MM-yyyy'
  size: PropTypes.oneOf(['XS', 'S', 'M']),
  isReadOnly: PropTypes.bool,
  event: TypeEvent,
  onChange: PropTypes.func,
  onRemove: PropTypes.func,
  limits: PropTypes.shape({
    minDate: PropTypes.object,
    maxDate: PropTypes.object
  }),
  allowedRanges: PropTypes.arrayOf(
    PropTypes.shape({
      from: PropTypes.instanceOf(Date).isRequired,
      to: PropTypes.instanceOf(Date).isRequired
    })
  ),
  highlightDates: PropTypes.array,
  hideTimePicker: PropTypes.bool
};

EventDateRangeInput.defaultProps = {
  appearance: 'silent',
  dateFormat: 'eee dd MMM',
  event: {
    date_type: 'RECURRING_DEFAULT'
  },
  limits: {},
  filterDate: null,
  hideTimePicker: false
};

export default EventDateRangeInput;
